import { useEffect } from 'react';
import type {
  AdminUser,
  AdminRole,
  AdminOrganization,
} from '../api/extensions/useAdminOps';
import { Button } from './ui/button';
import ManageUserRolesForm from './ManageUserRolesForm';

interface UserManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: AdminUser | null;
  availableRoles: AdminRole[];
  availableOrgs: AdminOrganization[];
  isSystemAdmin: boolean;
  managedOrgIds?: string[]; // undefined = unrestricted (ALL-org admin); array = scoped
  currentUserProfileId?: string; // logged-in admin's user_profiles.id (to block self-edit of system role)
}

function UserManagementModal({
  isOpen,
  onClose,
  user,
  availableRoles,
  availableOrgs,
  isSystemAdmin,
  managedOrgIds,
  currentUserProfileId,
}: UserManagementModalProps) {
  // Lock background scroll while modal is open
  useEffect(() => {
    if (!isOpen) return;
    const html = document.documentElement;
    html.style.setProperty('overflow-y', 'hidden', 'important');
    return () => {
      html.style.removeProperty('overflow-y');
    };
  }, [isOpen]);

  if (!isOpen || !user) return null;

  const displayName =
    [user.first_name, user.last_name].filter(Boolean).join(' ') ||
    user.primary_email ||
    'Unknown';
  const initials =
    ((user.first_name || '').charAt(0) + (user.last_name || '').charAt(0)).toUpperCase() || 'U';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Manage roles and permissions for ${displayName}`}
    >
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

      <div className="relative z-50 w-full max-w-3xl rounded-lg border bg-card text-card-foreground shadow-xl">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-accent flex items-center justify-center">
                <span className="text-sm font-medium text-accent-foreground">{initials}</span>
              </div>
              <div>
                <h2 className="text-xl font-semibold">{displayName}</h2>
                <p className="text-sm text-muted-foreground">
                  {user.primary_email}
                  {user.phone ? ` · ${user.phone}` : ''}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          <ManageUserRolesForm
            user={user}
            availableRoles={availableRoles}
            availableOrgs={availableOrgs}
            isSystemAdmin={isSystemAdmin}
            managedOrgIds={managedOrgIds}
            currentUserProfileId={currentUserProfileId}
          />
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

export default UserManagementModal;
